<template>
  <div class="termin-manager">
    <div class="manager-toolbar">
      <div class="manager-heading">
        <span class="manager-heading-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
        <div>
          <div class="manager-eyebrow">Terminverwaltung</div>
          <h2>Schulungen &amp; Termine</h2>
          <p>Plane Termine, Anmeldefenster und Teilnehmerplätze zentral.</p>
        </div>
      </div>

      <v-btn color="#58d388" depressed class="create-action" @click="openDialog()">
        <v-icon left>mdi-plus</v-icon>
        Neuer Termin
      </v-btn>
    </div>

    <div class="manager-filter">
      <span>Ansicht</span>
      <v-btn-toggle v-model="filter" mandatory dense rounded background-color="transparent" dark class="filter-toggle">
        <v-btn value="upcoming"><v-icon left small>mdi-calendar-arrow-right</v-icon>Kommend</v-btn>
        <v-btn value="past"><v-icon left small>mdi-history</v-icon>Vergangen</v-btn>
        <v-btn value="all"><v-icon left small>mdi-all-inclusive</v-icon>Alle</v-btn>
      </v-btn-toggle>
    </div>

    <v-row v-if="loading" class="session-list">
      <v-col v-for="n in 3" :key="n" cols="12" md="6" lg="4" class="session-column">
        <v-skeleton-loader type="card" dark></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-row v-else-if="filteredTermine.length" class="session-list">
      <v-col v-for="t in filteredTermine" :key="t._id" cols="12" md="6" lg="4" class="session-column">
        <v-card class="termin-tile h-100" dark flat>
          <div class="tile-header">
            <div class="date-badge">
              <div class="date-month">{{ monthShort(t.startsAt) }}</div>
              <div class="date-day">{{ day(t.startsAt) }}</div>
            </div>
            <div class="tile-title">
              <span>{{ formatWeekday(t.startsAt) }}</span>
              <h3>{{ t.title }}</h3>
              <small><v-icon>mdi-clock-outline</v-icon>{{ time(t.startsAt) }} – {{ time(t.endsAt) }}</small>
            </div>
            <span class="phase-pill" :class="phaseClass(t)">{{ phase(t) }}</span>
          </div>

          <v-card-text class="tile-content">
            <div class="tile-facts">
              <div>
                <span><v-icon>mdi-map-marker-outline</v-icon></span>
                <div>
                  <small>Ort</small><strong>{{ t.location || "Noch nicht festgelegt" }}</strong>
                </div>
              </div>
              <div>
                <span><v-icon>mdi-calendar-lock-outline</v-icon></span>
                <div>
                  <small>Anmeldung bis</small><strong>{{ formatDateTimeValue(t.registrationEnd) }}</strong>
                </div>
              </div>
            </div>
            <p class="tile-description">{{ t.description || "Keine Beschreibung hinterlegt." }}</p>

            <div class="capacity-box">
              <div class="capacity-heading">
                <span>Belegung</span>
                <strong>{{ t.registeredCount || 0 }} von {{ t.maxParticipants }} Plätzen</strong>
              </div>
              <v-progress-linear
                :value="((t.registeredCount || 0) / Math.max(1, t.maxParticipants)) * 100"
                color="#58d388"
                height="7"
                rounded
                background-color="rgba(255,255,255,0.08)"></v-progress-linear>
              <small>{{ availableSeatText(t) }}</small>
            </div>
          </v-card-text>

          <div class="tile-actions">
            <v-btn depressed class="participants-action" @click="openRegistrations(t)">
              <span class="participants-action-icon"><v-icon>mdi-account-group-outline</v-icon></span>
              <span class="participants-action-copy">
                <strong>Teilnehmer ansehen</strong>
                <small>{{ participantActionText(t) }}</small>
              </span>
              <v-icon class="participants-action-arrow">mdi-chevron-right</v-icon>
            </v-btn>
            <div class="tile-management-actions">
              <v-btn outlined class="edit-termin-action" @click="openDialog(t)">
                <v-icon left>mdi-pencil-outline</v-icon>
                Bearbeiten
              </v-btn>
              <v-btn text class="delete-termin-action" @click="confirmDelete(t)">
                <v-icon left>mdi-delete-outline</v-icon>
                Löschen
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else class="empty-state">
      <span class="empty-state-icon"><v-icon>mdi-calendar-blank-outline</v-icon></span>
      <h3>Keine Termine in dieser Ansicht</h3>
      <p>{{ emptyStateText }}</p>
      <v-btn v-if="filter !== 'all' && termine.length" text color="#58d388" class="empty-state-action" @click="filter = 'all'">
        Alle Termine anzeigen
      </v-btn>
    </div>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="920" persistent dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="termin-dialog-card">
        <header class="termin-dialog-header">
          <span class="termin-dialog-icon"
            ><v-icon>{{ editingId ? "mdi-calendar-edit" : "mdi-calendar-plus" }}</v-icon></span
          >
          <div class="termin-dialog-title">
            <div>Schulungsplan</div>
            <h2>{{ editingId ? "Termin bearbeiten" : "Neuen Termin erstellen" }}</h2>
            <p>Lege Zeit, Organisation und Anmeldefenster übersichtlich fest.</p>
          </div>
          <v-btn icon class="dialog-close" aria-label="Dialog schliessen" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>

        <v-card-text class="termin-dialog-body">
          <v-form ref="form" v-model="valid">
            <section class="form-section">
              <div class="form-section-heading">
                <span><v-icon>mdi-text-box-outline</v-icon></span>
                <div>
                  <h3>Grundinformationen</h3>
                  <p>Was wird angeboten und wo findet die Schulung statt?</p>
                </div>
              </div>
              <div class="form-grid form-grid--two">
                <v-text-field
                  v-model="form.title"
                  label="Titel"
                  prepend-inner-icon="mdi-format-title"
                  :rules="[(value) => !!value || 'Titel erforderlich']"
                  required
                  outlined
                  class="modern-field grid-span-full"></v-text-field>
                <v-textarea
                  v-model="form.description"
                  label="Beschreibung"
                  prepend-inner-icon="mdi-text-long"
                  outlined
                  rows="3"
                  class="modern-field dialog-textarea grid-span-full"></v-textarea>
                <v-text-field
                  v-model="form.location"
                  label="Ort"
                  prepend-inner-icon="mdi-map-marker-outline"
                  outlined
                  class="modern-field"></v-text-field>
                <v-text-field
                  v-model.number="form.maxParticipants"
                  label="Maximale Teilnehmerzahl"
                  prepend-inner-icon="mdi-account-multiple-outline"
                  type="number"
                  min="1"
                  :rules="[participantCountRule]"
                  outlined
                  class="modern-field"></v-text-field>
              </div>
            </section>

            <section class="form-section">
              <div class="form-section-heading">
                <span><v-icon>mdi-clock-outline</v-icon></span>
                <div>
                  <h3>Terminzeit</h3>
                  <p>Das Ende wird aus Start und Dauer berechnet und bleibt trotzdem bearbeitbar.</p>
                </div>
              </div>
              <div class="form-grid form-grid--time">
                <v-text-field
                  v-model="form.startsAt"
                  label="Startdatum und Zeit"
                  type="datetime-local"
                  prepend-inner-icon="mdi-calendar-start"
                  :rules="[(value) => !!value || 'Start erforderlich']"
                  outlined
                  class="modern-field"></v-text-field>
                <v-text-field
                  v-model.number="durationMinutes"
                  label="Dauer"
                  type="number"
                  min="1"
                  step="15"
                  suffix="Min."
                  prepend-inner-icon="mdi-timer-outline"
                  :rules="[durationRule]"
                  outlined
                  class="modern-field"></v-text-field>
                <v-text-field
                  v-model="form.endsAt"
                  label="Enddatum und Zeit"
                  type="datetime-local"
                  prepend-inner-icon="mdi-calendar-end"
                  :rules="[endAfterStartRule]"
                  outlined
                  class="modern-field"
                  @change="syncDurationFromEnd"></v-text-field>
              </div>
            </section>

            <section class="form-section">
              <div class="form-section-heading">
                <span><v-icon>mdi-tune-variant</v-icon></span>
                <div>
                  <h3>Teilnahmebedingungen</h3>
                  <p>Definiere Voraussetzungen, Stornofrist und Kursart.</p>
                </div>
              </div>
              <div class="form-grid form-grid--two">
                <v-select
                  v-model="form.requiredModules"
                  :items="allModules"
                  item-text="title"
                  item-value="_id"
                  label="Pflichtmodule als Voraussetzung"
                  prepend-inner-icon="mdi-book-check-outline"
                  multiple
                  chips
                  outlined
                  class="modern-field grid-span-full"></v-select>
                <v-text-field
                  v-model.number="form.cancellationDeadlineHours"
                  label="Stornofrist vor Beginn"
                  type="number"
                  min="0"
                  suffix="Std."
                  prepend-inner-icon="mdi-calendar-remove-outline"
                  :rules="[cancellationDeadlineRule]"
                  outlined
                  class="modern-field"></v-text-field>
                <div class="setting-control">
                  <div><strong>Auffrischungskurs</strong><small>Markiert den Termin als Wiederholungskurs.</small></div>
                  <v-switch v-model="form.isRefresher" color="#58d388" inset hide-details aria-label="Auffrischungskurs"></v-switch>
                </div>
              </div>
            </section>

            <section class="form-section form-section--registration">
              <div class="form-section-heading">
                <span><v-icon>mdi-account-clock-outline</v-icon></span>
                <div>
                  <h3>Anmeldefenster</h3>
                  <p>Nutze die sinnvollen Standardwerte oder lege eigene Zeitpunkte fest.</p>
                </div>
              </div>
              <div class="registration-grid">
                <div class="registration-option" :class="{ 'is-automatic': registrationStartsImmediately }">
                  <div class="automation-heading">
                    <div><strong>Beginn der Anmeldung</strong><small>Standardmässig direkt ab Aufschaltung.</small></div>
                    <v-switch
                      v-model="registrationStartsImmediately"
                      color="#58d388"
                      inset
                      hide-details
                      aria-label="Anmeldung ab Aufschaltung"
                      @change="handleRegistrationStartMode"></v-switch>
                  </div>
                  <div v-if="registrationStartsImmediately" class="automation-summary">
                    <span><v-icon>mdi-check-circle-outline</v-icon></span>
                    <div>
                      <small>{{ editingId ? "Aufgeschaltet am" : "Aufschaltung" }}</small>
                      <strong>{{ editingId ? formatDateTimeValue(form.registrationStart) : "Direkt nach dem Speichern" }}</strong>
                    </div>
                  </div>
                  <v-text-field
                    v-else
                    v-model="form.registrationStart"
                    label="Anmeldung ab"
                    type="datetime-local"
                    prepend-inner-icon="mdi-calendar-import"
                    :rules="[(value) => !!value || 'Anmeldebeginn erforderlich']"
                    hint="Individueller Anmeldebeginn."
                    persistent-hint
                    outlined
                    class="modern-field"></v-text-field>
                </div>

                <div class="registration-option" :class="{ 'is-automatic': registrationEndsAutomatically }">
                  <div class="automation-heading">
                    <div><strong>Ende der Anmeldung</strong><small>Standardmässig 24 Stunden vor Kursbeginn.</small></div>
                    <v-switch
                      v-model="registrationEndsAutomatically"
                      color="#58d388"
                      inset
                      hide-details
                      aria-label="Anmeldeschluss einen Tag vorher"
                      @change="handleRegistrationEndMode"></v-switch>
                  </div>
                  <div v-if="registrationEndsAutomatically" class="automation-summary">
                    <span><v-icon>mdi-calendar-check-outline</v-icon></span>
                    <div>
                      <small>Automatischer Anmeldeschluss</small>
                      <strong>{{ formatDateTimeValue(form.registrationEnd) }}</strong>
                    </div>
                  </div>
                  <v-text-field
                    v-else
                    v-model="form.registrationEnd"
                    label="Anmeldung bis"
                    type="datetime-local"
                    prepend-inner-icon="mdi-calendar-export"
                    :rules="[registrationEndRule]"
                    hint="Individueller Anmeldeschluss."
                    persistent-hint
                    outlined
                    class="modern-field"></v-text-field>
                </div>
              </div>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="termin-dialog-actions">
          <v-btn text class="cancel-action" @click="dialog = false">Abbrechen</v-btn>
          <v-btn color="#58d388" depressed class="save-action" @click="saveTermin" :disabled="!valid" :loading="saving">
            <v-icon left>mdi-content-save-outline</v-icon>
            {{ editingId ? "Änderungen speichern" : "Termin erstellen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Registrations Dialog -->
    <v-dialog v-model="regDialog" max-width="920" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="participants-dialog-card">
        <header class="participants-dialog-header">
          <span class="participants-dialog-icon"><v-icon>mdi-account-group-outline</v-icon></span>
          <div class="participants-dialog-title">
            <div>Teilnehmerverwaltung</div>
            <h2>{{ activeTitle || "Schulung" }}</h2>
            <p>{{ activeTerminSummary }}</p>
          </div>
          <v-btn icon class="dialog-close" aria-label="Teilnehmerdialog schliessen" @click="regDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>

        <div class="registration-stats" aria-label="Teilnehmerstatus Übersicht">
          <div class="registration-stat registration-stat--active">
            <span><v-icon>mdi-account-check-outline</v-icon></span>
            <div>
              <small>Angemeldet</small><strong>{{ countByStatus("registered") }}</strong>
            </div>
          </div>
          <div class="registration-stat registration-stat--waiting">
            <span><v-icon>mdi-account-clock-outline</v-icon></span>
            <div>
              <small>Warteliste</small><strong>{{ countByStatus("waiting_list") }}</strong>
            </div>
          </div>
          <div class="registration-stat registration-stat--attended">
            <span><v-icon>mdi-check-decagram-outline</v-icon></span>
            <div>
              <small>Besucht</small><strong>{{ countByStatus("attended") }}</strong>
            </div>
          </div>
        </div>

        <v-card-text class="participants-dialog-body">
          <div class="participants-toolbar">
            <v-btn-toggle v-model="regFilter" mandatory dense rounded background-color="transparent" dark class="registration-filter">
              <v-btn value="active"><v-icon left small>mdi-account-check-outline</v-icon>Aktiv</v-btn>
              <v-btn value="all"><v-icon left small>mdi-account-group-outline</v-icon>Alle</v-btn>
              <v-btn value="cancelled"><v-icon left small>mdi-account-cancel-outline</v-icon>Storniert</v-btn>
            </v-btn-toggle>
            <v-btn outlined color="#58d388" class="export-action" @click="exportExcel" :loading="exportingExcel">
              <v-icon left>mdi-microsoft-excel</v-icon>
              Excel exportieren
            </v-btn>
          </div>

          <div v-if="loadingRegistrations" class="participants-loading" aria-label="Teilnehmer werden geladen">
            <v-skeleton-loader v-for="index in 3" :key="index" dark type="list-item-avatar-three-line"></v-skeleton-loader>
          </div>

          <div v-else-if="!filteredRegistrations.length" class="participants-empty">
            <span><v-icon>mdi-account-off-outline</v-icon></span>
            <h3>Keine Teilnehmer in dieser Ansicht</h3>
            <p>{{ registrationEmptyText }}</p>
          </div>

          <div v-else class="participants-list">
            <article v-for="r in filteredRegistrations" :key="r._id" class="participant-row">
              <v-avatar size="44" class="participant-avatar"
                ><span>{{ initials(r.user) }}</span></v-avatar
              >
              <div class="participant-copy">
                <strong>{{ participantName(r.user) }}</strong>
                <span class="participant-meta">
                  <v-icon>mdi-map-marker-outline</v-icon>
                  {{ (r.user && r.user.studio) || "Kein Studio" }}
                  <template v-if="r.registeredAt"> · angemeldet {{ formatShort(r.registeredAt) }}</template>
                </span>
                <span class="participant-meal" :class="{ 'is-empty': !r.mealPreference || r.mealPreference === 'none' }">
                  <v-icon>mdi-silverware-fork-knife</v-icon>
                  {{ mealPreferenceLabel(r.mealPreference) }}
                </span>
              </div>
              <span class="participant-status" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
              <div class="participant-actions">
                <template v-if="r.status !== 'cancelled'">
                  <v-btn
                    outlined
                    class="attendance-action attendance-action--present"
                    :disabled="r.status === 'attended' || Boolean(actionBusy[r._id])"
                    :loading="actionBusy[r._id] === 'attended'"
                    @click="markAttended(r, true)">
                    <v-icon left>mdi-check-circle-outline</v-icon>
                    Anwesend
                  </v-btn>
                  <v-btn
                    outlined
                    class="attendance-action attendance-action--absent"
                    :disabled="r.status === 'no_show' || Boolean(actionBusy[r._id])"
                    :loading="actionBusy[r._id] === 'no_show'"
                    @click="markAttended(r, false)">
                    <v-icon left>mdi-close-circle-outline</v-icon>
                    Nicht da
                  </v-btn>
                  <v-btn
                    v-if="r.status === 'attended' || r.status === 'no_show'"
                    text
                    class="participant-reset-action"
                    :loading="actionBusy[r._id] === 'reset'"
                    @click="resetAttendance(r)">
                    <v-icon left>mdi-restore</v-icon>
                    Zurücksetzen
                  </v-btn>
                  <v-btn
                    text
                    class="participant-cancel-action"
                    :disabled="Boolean(actionBusy[r._id])"
                    :loading="actionBusy[r._id] === 'cancel'"
                    @click="adminCancel(r)">
                    <v-icon left>mdi-account-remove-outline</v-icon>
                    Abmelden
                  </v-btn>
                </template>
              </div>
            </article>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460" persistent dark>
      <v-card class="delete-dialog-card">
        <span class="delete-dialog-icon"><v-icon>mdi-delete-alert-outline</v-icon></span>
        <div class="delete-dialog-copy">
          <div>Termin löschen</div>
          <h2>{{ deleteTarget ? deleteTarget.title : "Termin" }}</h2>
          <p>Der Termin wird aus dem Schulungsplan entfernt. Dieser Schritt kann nicht rückgängig gemacht werden.</p>
        </div>
        <v-card-actions class="delete-dialog-actions">
          <v-btn text class="cancel-action" @click="closeDeleteDialog">Abbrechen</v-btn>
          <v-btn color="#ef5350" depressed class="confirm-delete-action" :loading="deleting" @click="deleteTermin">
            <v-icon left>mdi-delete-outline</v-icon>
            Endgültig löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

const MINUTE_MS = 60 * 1000
function parseDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function toLocalInputValue(value) {
  const date = parseDate(value)
  if (!date) return ""
  const pad = (number) => String(number).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function oneDayBefore(value) {
  const date = parseDate(value)
  if (!date) return null
  const previousDay = new Date(date)
  previousDay.setDate(previousDay.getDate() - 1)
  return previousDay
}

function defaultSchedule() {
  const registrationStart = new Date()
  registrationStart.setSeconds(0, 0)

  const startsAt = new Date(registrationStart)
  startsAt.setDate(startsAt.getDate() + 1)
  const minuteRemainder = startsAt.getMinutes() % 15
  if (minuteRemainder) startsAt.setMinutes(startsAt.getMinutes() + 15 - minuteRemainder)

  const endsAt = new Date(startsAt.getTime() + 60 * MINUTE_MS)
  return { registrationStart, startsAt, endsAt, registrationEnd: oneDayBefore(startsAt) }
}

export default {
  name: "TerminManager",
  data() {
    return {
      termine: [],
      allModules: [],
      loading: false,
      saving: false,
      deleting: false,
      loadingRegistrations: false,
      dialog: false,
      deleteDialog: false,
      deleteTarget: null,
      regDialog: false,
      registrations: [],
      activeTermin: null,
      activeTitle: "",
      regFilter: "active",
      actionBusy: {},
      exportingExcel: false,
      valid: false,
      editingId: null,
      filter: "upcoming",
      durationMinutes: 60,
      registrationStartsImmediately: true,
      registrationEndsAutomatically: true,
      isSyncingTimeFields: false,
      form: this.emptyForm(),
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    filteredTermine() {
      const now = new Date()
      const list = [...(this.termine || [])].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      if (this.filter === "upcoming") return list.filter((t) => new Date(t.startsAt) >= now)
      if (this.filter === "past") return list.filter((t) => new Date(t.startsAt) < now).reverse()
      return list
    },
    emptyStateText() {
      if (this.filter === "upcoming") return "Plane einen neuen Termin oder prüfe die vergangenen Schulungen."
      if (this.filter === "past") return "Abgeschlossene Schulungen erscheinen automatisch in dieser Ansicht."
      return "Erstelle den ersten Termin für den Schulungsplan."
    },
    activeTerminSummary() {
      if (!this.activeTermin) return "Termindetails werden geladen"
      const date = new Date(this.activeTermin.startsAt)
      const dateLabel = Number.isNaN(date.getTime())
        ? "Datum noch offen"
        : date.toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
      const timeLabel = `${this.time(this.activeTermin.startsAt)} – ${this.time(this.activeTermin.endsAt)}`
      return [dateLabel, timeLabel, this.activeTermin.location || "Ort noch offen"].filter(Boolean).join(" · ")
    },
    registrationEmptyText() {
      if (this.regFilter === "cancelled") return "Für diesen Termin gibt es keine stornierten Anmeldungen."
      if (this.regFilter === "active") return "Aktuell sind keine aktiven Anmeldungen vorhanden."
      return "Für diesen Termin sind noch keine Teilnehmer erfasst."
    },
    filteredRegistrations() {
      if (!this.registrations) return []
      if (this.regFilter === "all") return this.registrations
      if (this.regFilter === "cancelled") return this.registrations.filter((r) => r.status === "cancelled")
      return this.registrations.filter((r) => r.status !== "cancelled")
    },
  },
  watch: {
    "form.startsAt"() {
      if (this.isSyncingTimeFields) return
      this.syncEndFromDuration()
      if (this.registrationEndsAutomatically) this.syncRegistrationEnd()
    },
    durationMinutes() {
      if (this.isSyncingTimeFields) return
      this.syncEndFromDuration()
    },
  },
  mounted() {
    this.loadTermine()
    this.loadModules()
  },
  methods: {
    emptyForm() {
      const schedule = defaultSchedule()
      return {
        title: "",
        description: "",
        startsAt: toLocalInputValue(schedule.startsAt),
        endsAt: toLocalInputValue(schedule.endsAt),
        location: "",
        maxParticipants: 10,
        requiredModules: [],
        cancellationDeadlineHours: 72,
        isRefresher: false,
        registrationStart: toLocalInputValue(schedule.registrationStart),
        registrationEnd: toLocalInputValue(schedule.registrationEnd),
      }
    },
    durationRule(value) {
      const duration = Number(value)
      return (Number.isFinite(duration) && duration >= 1 && duration <= 10080) || "Dauer zwischen 1 Minute und 7 Tagen eingeben"
    },
    participantCountRule(value) {
      const count = Number(value)
      return (Number.isInteger(count) && count >= 1) || "Mindestens eine Person erforderlich"
    },
    cancellationDeadlineRule(value) {
      const hours = Number(value)
      return (Number.isFinite(hours) && hours >= 0) || "Stornofrist darf nicht negativ sein"
    },
    endAfterStartRule(value) {
      const start = parseDate(this.form.startsAt)
      const end = parseDate(value)
      if (!end) return "Ende erforderlich"
      if (!start) return true
      return end.getTime() > start.getTime() || "Das Ende muss nach dem Start liegen"
    },
    registrationEndRule(value) {
      const registrationStart = parseDate(this.form.registrationStart)
      const registrationEnd = parseDate(value)
      const courseStart = parseDate(this.form.startsAt)
      if (!registrationEnd) return "Anmeldeschluss erforderlich"
      if (registrationStart && registrationEnd.getTime() <= registrationStart.getTime()) {
        return "Der Anmeldeschluss muss nach dem Anmeldebeginn liegen"
      }
      if (courseStart && registrationEnd.getTime() >= courseStart.getTime()) {
        return "Der Anmeldeschluss muss vor dem Kursbeginn liegen"
      }
      return true
    },
    syncEndFromDuration() {
      const start = parseDate(this.form.startsAt)
      const duration = Number(this.durationMinutes)
      if (!start || !Number.isFinite(duration) || duration < 1) return
      this.form.endsAt = toLocalInputValue(new Date(start.getTime() + duration * MINUTE_MS))
    },
    syncDurationFromEnd() {
      const start = parseDate(this.form.startsAt)
      const end = parseDate(this.form.endsAt)
      if (!start || !end || end.getTime() <= start.getTime()) return
      this.isSyncingTimeFields = true
      this.durationMinutes = Math.round((end.getTime() - start.getTime()) / MINUTE_MS)
      this.$nextTick(() => {
        this.isSyncingTimeFields = false
      })
    },
    syncRegistrationEnd() {
      const registrationEnd = oneDayBefore(this.form.startsAt)
      if (registrationEnd) this.form.registrationEnd = toLocalInputValue(registrationEnd)
    },
    handleRegistrationStartMode(enabled) {
      if (enabled) this.form.registrationStart = toLocalInputValue(new Date())
    },
    handleRegistrationEndMode(enabled) {
      if (enabled) this.syncRegistrationEnd()
    },
    wasPublishedImmediately(item) {
      const registrationStart = parseDate(item.registrationStart)
      const createdAt = parseDate(item.createdAt)
      return Boolean(registrationStart && createdAt && Math.abs(registrationStart.getTime() - createdAt.getTime()) <= 5 * MINUTE_MS)
    },
    hasAutomaticRegistrationEnd(item) {
      const registrationEnd = parseDate(item.registrationEnd)
      const expectedEnd = oneDayBefore(item.startsAt)
      return Boolean(registrationEnd && expectedEnd && Math.abs(registrationEnd.getTime() - expectedEnd.getTime()) <= MINUTE_MS)
    },
    getDurationMinutes(startsAt, endsAt) {
      const start = parseDate(startsAt)
      const end = parseDate(endsAt)
      if (!start || !end || end.getTime() <= start.getTime()) return 60
      return Math.max(1, Math.round((end.getTime() - start.getTime()) / MINUTE_MS))
    },
    formatDateTimeValue(value) {
      const date = parseDate(value)
      if (!date) return "Noch nicht festgelegt"
      return date.toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadTermine() {
      this.loading = true
      try {
        this.termine = (await api.akademie.fetchTermine()) || []
      } catch (e) {
        this.notify("error", "Termine konnten nicht geladen werden")
      } finally {
        this.loading = false
      }
    },
    async loadModules() {
      try {
        this.allModules = (await api.akademie.fetchModules()) || []
      } catch (e) {
        /* ignore */
      }
    },
    monthShort(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH", { month: "short" }).replace(".", "")
    },
    day(d) {
      if (!d) return ""
      return new Date(d).getDate()
    },
    time(d) {
      if (!d) return ""
      return new Date(d).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    formatWeekday(value) {
      const date = parseDate(value)
      if (!date) return "Termin"
      return date.toLocaleDateString("de-CH", { weekday: "long" })
    },
    availableSeatText(item) {
      const maxParticipants = Math.max(0, Number(item.maxParticipants) || 0)
      const registeredCount = Math.max(0, Number(item.registeredCount) || 0)
      const availableSeats = Math.max(0, maxParticipants - registeredCount)
      if (availableSeats === 0) return "Ausgebucht · weitere Anmeldungen kommen auf die Warteliste"
      return `${availableSeats} ${availableSeats === 1 ? "Platz" : "Plätze"} verfügbar`
    },
    participantActionText(item) {
      const count = Math.max(0, Number(item.registeredCount) || 0)
      return `${count} ${count === 1 ? "Person angemeldet" : "Personen angemeldet"}`
    },
    participantName(user) {
      if (!user) return "Unbekannte Person"
      return [user.vorname, user.nachname].filter(Boolean).join(" ") || "Unbekannte Person"
    },
    mealPreferenceLabel(value) {
      const labels = {
        none: "Keine Angabe",
        meat: "Fleisch",
        no_pork: "Kein Schweinefleisch",
        vegetarian: "Vegetarisch",
        vegan: "Vegan",
      }
      return labels[value] || labels.none
    },
    initials(u) {
      if (!u) return "?"
      return ((u.vorname || "?")[0] + (u.nachname ? u.nachname[0] : "")).toUpperCase()
    },
    phase(t) {
      if (!t.registrationStart || !t.registrationEnd) return "—"
      const now = new Date()
      if (now < new Date(t.registrationStart)) return "Geplant"
      if (now > new Date(t.registrationEnd)) return "Geschlossen"
      return "Geöffnet"
    },
    phaseClass(t) {
      const p = this.phase(t)
      if (p === "Geöffnet") return "ribbon-success"
      if (p === "Geplant") return "ribbon-warning"
      return "ribbon-error"
    },
    statusClass(status) {
      if (status === "registered") return "status-registered"
      if (status === "waiting_list") return "status-waiting"
      if (status === "attended") return "status-attended"
      if (status === "no_show") return "status-no-show"
      return "status-cancelled"
    },
    openDialog(item = null) {
      this.isSyncingTimeFields = true
      if (item) {
        this.editingId = item._id
        this.durationMinutes = this.getDurationMinutes(item.startsAt, item.endsAt)
        this.registrationStartsImmediately = this.wasPublishedImmediately(item)
        this.registrationEndsAutomatically = this.hasAutomaticRegistrationEnd(item)
        this.form = {
          title: item.title || "",
          description: item.description || "",
          startsAt: toLocalInputValue(item.startsAt),
          endsAt: toLocalInputValue(item.endsAt),
          location: item.location || "",
          maxParticipants: Number(item.maxParticipants) >= 1 ? Number(item.maxParticipants) : 10,
          requiredModules: item.requiredModules ? item.requiredModules.map((m) => m._id || m) : [],
          cancellationDeadlineHours:
            item.cancellationDeadlineHours !== undefined && item.cancellationDeadlineHours !== null ? Number(item.cancellationDeadlineHours) : 72,
          isRefresher: !!item.isRefresher,
          registrationStart: toLocalInputValue(item.registrationStart),
          registrationEnd: toLocalInputValue(item.registrationEnd),
        }
      } else {
        this.editingId = null
        this.durationMinutes = 60
        this.registrationStartsImmediately = true
        this.registrationEndsAutomatically = true
        this.form = this.emptyForm()
      }
      this.dialog = true
      this.$nextTick(() => {
        this.isSyncingTimeFields = false
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    async saveTermin() {
      this.syncEndFromDuration()
      if (this.registrationStartsImmediately && !this.editingId) {
        this.form.registrationStart = toLocalInputValue(new Date())
      }
      if (this.registrationEndsAutomatically) this.syncRegistrationEnd()
      if (!this.$refs.form.validate()) return

      // Wir erstellen eine Kopie der Form-Daten für den Versand
      const payload = { ...this.form }

      // Wir konvertieren die lokalen Strings explizit via Moment
      // Damit wird das korrekte Date-Objekt erzeugt, das beim
      // JSON-Stringify den richtigen UTC-Offset mitgibt.
      payload.startsAt = this.$moment(this.form.startsAt).toDate()
      payload.endsAt = this.$moment(this.form.endsAt).toDate()
      payload.registrationStart = this.$moment(this.form.registrationStart).toDate()
      payload.registrationEnd = this.$moment(this.form.registrationEnd).toDate()

      this.saving = true
      try {
        if (this.editingId) {
          await api.akademie.updateTermin(this.editingId, payload)
          this.notify("success", "Termin aktualisiert")
        } else {
          await api.akademie.createTermin(payload)
          this.notify("success", "Termin erstellt")
        }
        this.dialog = false
        this.loadTermine()
      } catch (e) {
        this.notify("error", "Speichern fehlgeschlagen")
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    closeDeleteDialog() {
      if (this.deleting) return
      this.deleteDialog = false
      this.deleteTarget = null
    },
    async deleteTermin() {
      if (!this.deleteTarget) return
      this.deleting = true
      try {
        await api.akademie.deleteTermin(this.deleteTarget._id)
        this.deleteDialog = false
        this.deleteTarget = null
        await this.loadTermine()
        this.notify("success", "Termin gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      } finally {
        this.deleting = false
      }
    },
    async openRegistrations(t) {
      this.regDialog = true
      this.regFilter = "active"
      this.activeTitle = t.title
      this.activeTermin = t
      this.registrations = []
      this.actionBusy = {}
      this.loadingRegistrations = true
      try {
        this.registrations = (await api.akademie.fetchRegistrations(t._id)) || []
      } catch (e) {
        this.notify("error", "Anmeldungen konnten nicht geladen werden")
      } finally {
        this.loadingRegistrations = false
      }
    },
    countByStatus(status) {
      return (this.registrations || []).filter((r) => r.status === status).length
    },
    statusLabel(s) {
      const map = { registered: "Angemeldet", waiting_list: "Warteliste", cancelled: "Storno", attended: "Besucht", no_show: "Nicht da" }
      return map[s] || s
    },
    formatShort(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH")
    },
    setBusy(id, action) {
      this.$set(this.actionBusy, id, action)
    },
    clearBusy(id) {
      this.$set(this.actionBusy, id, null)
    },
    async markAttended(r, attended) {
      if (!this.activeTermin) return
      this.setBusy(r._id, attended ? "attended" : "no_show")
      try {
        const updated = await api.akademie.markAttendance(this.activeTermin._id, r._id, attended)
        Object.assign(r, updated)
        this.notify("success", attended ? "Als anwesend markiert" : "Als nicht erschienen markiert")
      } catch (e) {
        this.notify("error", "Markieren fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async resetAttendance(r) {
      this.setBusy(r._id, "reset")
      try {
        const updated = await api.akademie.markAttendance(this.activeTermin._id, r._id, null)
        Object.assign(r, updated)
      } catch (e) {
        this.notify("error", "Zurücksetzen fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async adminCancel(r) {
      if (!confirm(`Anmeldung von ${r.user ? r.user.vorname + " " + r.user.nachname : "?"} stornieren?`)) return
      this.setBusy(r._id, "cancel")
      try {
        const updated = await api.akademie.adminCancelRegistration(this.activeTermin._id, r._id)
        Object.assign(r, updated)
        this.notify("success", "Anmeldung storniert")
        // Liste neu laden, weil ggf. jemand von der Warteliste nachrückt
        this.registrations = (await api.akademie.fetchRegistrations(this.activeTermin._id)) || []
        this.loadTermine()
      } catch (e) {
        this.notify("error", "Stornieren fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async exportExcel() {
      if (!this.activeTermin) return
      this.exportingExcel = true
      try {
        await api.akademie.exportTerminExcel(this.activeTermin._id, this.activeTermin.title)
        this.notify("success", "Export wurde gestartet")
      } catch (e) {
        this.notify("error", "Excel-Export fehlgeschlagen")
      } finally {
        this.exportingExcel = false
      }
    },
  },
}
</script>

<style scoped>
.termin-manager {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  color: var(--text);
  font-size: 1rem;
}

.manager-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.09), transparent 55%), var(--surface);
}

.manager-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.875rem;
}

.manager-heading > div {
  min-width: 0;
}

.manager-heading-icon {
  display: grid;
  flex: 0 0 3rem;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.9375rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.manager-heading-icon .v-icon {
  color: currentColor;
}

.manager-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.manager-heading h2 {
  margin: 0.125rem 0 0;
  color: var(--text);
  font-size: 1.375rem;
  line-height: 1.25;
}

.manager-heading p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.45;
}

.create-action {
  min-height: 2.75rem !important;
  flex: 0 0 auto;
  padding: 0 1rem !important;
  border-radius: 0.8125rem !important;
  color: #07120b !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.manager-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0.75rem 0 1rem;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 0.9375rem;
  background: rgba(255, 255, 255, 0.022);
}

.manager-filter > span {
  padding-left: 0.5rem;
  color: #d4d4d8;
  font-size: 0.875rem;
  font-weight: 700;
}

.filter-toggle {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.filter-toggle ::v-deep .v-btn {
  min-width: 0 !important;
  min-height: 2.75rem !important;
  padding: 0 0.75rem !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
  color: #a3a3a8 !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0.04em;
}

.filter-toggle ::v-deep .v-btn--active {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.12) !important;
}

.session-list {
  margin: -0.375rem !important;
}

.session-column {
  padding: 0.375rem !important;
}

.h-100 {
  height: 100%;
}

.termin-tile {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.035), transparent 42%), rgba(29, 31, 30, 0.92) !important;
  box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.12);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.termin-tile:hover {
  border-color: var(--border-strong);
  box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.2);
}

.tile-header {
  display: grid;
  grid-template-columns: 3.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(255, 255, 255, 0.018);
}

.phase-pill {
  align-self: start;
  padding: 0.25rem 0.625rem;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.ribbon-success {
  color: #7ee5a6;
}

.ribbon-warning {
  color: #f4d06f;
}

.ribbon-error {
  color: #c7c7cb;
}

.date-badge {
  display: grid;
  width: 3.5rem;
  min-height: 3.75rem;
  place-content: center;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.12);
  text-align: center;
}

.date-month {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.date-day {
  color: #ffffff;
  font-size: 1.375rem;
  font-weight: 800;
  line-height: 1.1;
}

.tile-title {
  min-width: 0;
}

.tile-title > span {
  display: block;
  overflow: hidden;
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 750;
  line-height: 1.3;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
}

.tile-title h3 {
  margin: 0.125rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.125rem;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-title small {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  margin-top: 0.25rem;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.3;
}

.tile-title small .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.tile-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 1rem !important;
}

.tile-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.tile-facts > div {
  display: grid;
  min-width: 0;
  grid-template-columns: 2rem minmax(0, 1fr);
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.12);
}

.tile-facts > div > span {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 0.625rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.tile-facts > div > span .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.tile-facts > div > div {
  display: grid;
  min-width: 0;
}

.tile-facts small,
.capacity-heading span,
.capacity-box > small {
  color: #8f9095;
  font-size: 0.75rem;
  line-height: 1.3;
}

.tile-facts strong {
  margin-top: 0.125rem;
  overflow: hidden;
  color: #d4d4d8;
  font-size: 0.75rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-description {
  display: -webkit-box;
  min-height: 2.625rem;
  margin: 0.875rem 0;
  overflow: hidden;
  color: #b8b8bd;
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.capacity-box {
  margin-top: auto;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(255, 255, 255, 0.025);
}

.capacity-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.capacity-heading strong {
  color: #d4d4d8;
  font-size: 0.75rem;
  line-height: 1.3;
}

.capacity-box > small {
  display: block;
  margin-top: 0.4375rem;
}

.tile-actions {
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(0, 0, 0, 0.12);
}

.participants-action {
  width: 100%;
  height: auto !important;
  min-height: 3.75rem !important;
  padding: 0.5rem 0.625rem !important;
  border: 1px solid rgba(88, 211, 136, 0.26) !important;
  border-radius: 0.8125rem !important;
  color: #f4f4f5 !important;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.15), rgba(0, 150, 64, 0.06)) !important;
  letter-spacing: 0;
  text-transform: none;
}

.participants-action ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
  gap: 0.625rem;
}

.participants-action-icon {
  display: grid;
  flex: 0 0 2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.17);
}

.participants-action-icon .v-icon,
.participants-action-arrow {
  color: currentColor !important;
}

.participants-action-copy {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
}

.participants-action-copy strong {
  font-size: 0.875rem;
  line-height: 1.3;
}

.participants-action-copy small {
  margin-top: 0.125rem;
  color: #9edbb5;
  font-size: 0.75rem;
  line-height: 1.3;
}

.participants-action-arrow {
  margin-left: auto;
}

.tile-management-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.edit-termin-action,
.delete-termin-action {
  min-width: 0 !important;
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.edit-termin-action {
  border-color: rgba(125, 211, 252, 0.24) !important;
  color: #9bdcf8 !important;
}

.delete-termin-action {
  color: #ff9aa4 !important;
}

.empty-state {
  display: flex;
  min-height: 15rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.02);
  text-align: center;
}

.empty-state-icon {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #7b7b80;
  background: rgba(255, 255, 255, 0.055);
}

.empty-state-icon .v-icon {
  color: currentColor;
  font-size: 2rem;
}

.empty-state h3 {
  margin: 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.35;
}

.empty-state p {
  max-width: 28rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.empty-state-action {
  min-height: 2.75rem !important;
  margin-top: 0.625rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.termin-dialog-card {
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 1.375rem !important;
  background: #171918 !important;
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.52) !important;
}

.termin-dialog-header {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) 2.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.13), transparent 58%), #1b1e1c;
}

.termin-dialog-icon,
.form-section-heading > span {
  display: grid;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.termin-dialog-icon {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 0.9375rem;
}

.termin-dialog-icon .v-icon,
.form-section-heading > span .v-icon {
  color: currentColor;
}

.termin-dialog-title {
  min-width: 0;
}

.termin-dialog-title > div {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.termin-dialog-title h2 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.25;
}

.termin-dialog-title p {
  margin: 0.1875rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.4;
}

.dialog-close {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.termin-dialog-body {
  padding: 1.125rem !important;
  background: #141615;
}

.form-section {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 1.0625rem;
  background: rgba(255, 255, 255, 0.025);
}

.form-section + .form-section {
  margin-top: 0.875rem;
}

.form-section--registration {
  border-color: rgba(0, 150, 64, 0.18);
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.05), transparent 55%), rgba(255, 255, 255, 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.form-section-heading > span {
  flex: 0 0 2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
}

.form-section-heading h3 {
  margin: 0;
  color: #f4f4f5;
  font-size: 1rem;
  line-height: 1.3;
}

.form-section-heading p {
  margin: 0.1875rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.4;
}

.form-grid {
  display: grid;
  gap: 0.75rem;
}

.form-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-grid--time {
  grid-template-columns: minmax(0, 1.35fr) minmax(8.5rem, 0.65fr) minmax(0, 1.35fr);
}

.grid-span-full {
  grid-column: 1 / -1;
}

.modern-field {
  min-width: 0;
  margin: 0 !important;
}

.modern-field ::v-deep .v-input__slot {
  min-height: 3.25rem !important;
  margin-bottom: 0 !important;
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.032) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.modern-field ::v-deep .v-messages {
  font-size: 0.875rem;
}

.modern-field ::v-deep input,
.modern-field ::v-deep textarea,
.modern-field ::v-deep .v-select__selection {
  color: #f4f4f5 !important;
  font-size: 1rem;
}

.modern-field ::v-deep input::-webkit-calendar-picker-indicator {
  opacity: 0.72;
  filter: invert(1);
}

.modern-field ::v-deep .v-input__prepend-inner .v-icon {
  color: #8f9095 !important;
}

.modern-field.v-input--is-readonly ::v-deep .v-input__slot {
  border-color: rgba(88, 211, 136, 0.16) !important;
  background: rgba(0, 150, 64, 0.055) !important;
}

.dialog-textarea ::v-deep .v-input__slot {
  align-items: flex-start;
  padding-top: 0.375rem !important;
}

.setting-control,
.automation-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.setting-control {
  min-height: 3.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.8125rem;
  background: rgba(255, 255, 255, 0.025);
}

.setting-control > div,
.automation-heading > div {
  display: grid;
  min-width: 0;
}

.setting-control strong,
.automation-heading strong {
  color: #e4e4e7;
  font-size: 0.875rem;
  line-height: 1.3;
}

.setting-control small,
.automation-heading small {
  margin-top: 0.1875rem;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.35;
}

.setting-control ::v-deep .v-input--switch,
.automation-heading ::v-deep .v-input--switch {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}

.registration-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.registration-option {
  min-width: 0;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.9375rem;
  background: rgba(0, 0, 0, 0.12);
}

.registration-option.is-automatic {
  border-color: rgba(0, 150, 64, 0.25);
  background: rgba(0, 150, 64, 0.045);
}

.registration-option .modern-field {
  margin-top: 0.875rem !important;
}

.automation-summary {
  display: grid;
  min-height: 4.25rem;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.875rem;
  padding: 0.75rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 0.8125rem;
  background: rgba(0, 150, 64, 0.075);
}

.automation-summary > span {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.automation-summary > span .v-icon {
  color: currentColor;
}

.automation-summary > div {
  display: grid;
  min-width: 0;
}

.automation-summary small {
  color: #9edbb5;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
}

.automation-summary strong {
  margin-top: 0.1875rem;
  color: #f4f4f5;
  font-size: 0.875rem;
  line-height: 1.35;
}

.termin-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  background: #1b1e1c;
}

.cancel-action,
.save-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.cancel-action {
  color: #c7c7cb !important;
}

.save-action {
  padding: 0 1rem !important;
  color: #07120b !important;
}

.participants-dialog-card {
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 1.375rem !important;
  background: #151716 !important;
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.52) !important;
}

.participants-dialog-header {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) 2.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.14), transparent 58%), #1b1e1c;
}

.participants-dialog-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 0.9375rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.participants-dialog-icon .v-icon {
  color: currentColor;
}

.participants-dialog-title {
  min-width: 0;
}

.participants-dialog-title > div {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.participants-dialog-title h2 {
  margin: 0.125rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participants-dialog-title p {
  margin: 0.1875rem 0 0;
  overflow: hidden;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.registration-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(255, 255, 255, 0.018);
}

.registration-stat {
  display: grid;
  min-width: 0;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.8125rem;
  background: rgba(255, 255, 255, 0.025);
}

.registration-stat > span {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.07);
}

.registration-stat > span .v-icon {
  color: currentColor;
}

.registration-stat--active > span {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.registration-stat--waiting > span {
  color: #f4d06f;
  background: rgba(245, 158, 11, 0.12);
}

.registration-stat--attended > span {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.12);
}

.registration-stat > div {
  display: grid;
  min-width: 0;
}

.registration-stat small {
  overflow: hidden;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.registration-stat strong {
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.2;
}

.participants-dialog-body {
  padding: 1rem 1.125rem !important;
  background: #141615;
}

.participants-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
}

.registration-filter {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.75rem !important;
  background: rgba(0, 0, 0, 0.15) !important;
}

.registration-filter ::v-deep .v-btn {
  min-height: 2.75rem !important;
  padding: 0 0.75rem !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
  color: #a3a3a8 !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
}

.registration-filter ::v-deep .v-btn--active {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.16) !important;
}

.export-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.participants-list {
  display: grid;
  gap: 0.625rem;
}

.participants-loading {
  display: grid;
  gap: 0.625rem;
}

.participants-loading ::v-deep .v-skeleton-loader__list-item-avatar-three-line {
  min-height: 6rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.9375rem;
  background: rgba(255, 255, 255, 0.025);
}

.participant-row {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.9375rem;
  background: rgba(255, 255, 255, 0.027);
}

.participant-avatar {
  color: #ffffff !important;
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.28), rgba(0, 150, 64, 0.12)) !important;
}

.participant-avatar span {
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 800;
}

.participant-copy {
  display: grid;
  min-width: 0;
}

.participant-copy > strong {
  overflow: hidden;
  color: #f4f4f5;
  font-size: 0.875rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participant-copy > span {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.1875rem;
  overflow: hidden;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participant-copy > span .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.participant-copy > .participant-meal {
  width: max-content;
  max-width: 100%;
  margin-top: 0.4375rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 999px;
  overflow: visible;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.1);
  font-weight: 700;
  text-overflow: clip;
  white-space: normal;
}

.participant-copy > .participant-meal .v-icon {
  color: currentColor;
}

.participant-copy > .participant-meal.is-empty {
  border-color: rgba(255, 255, 255, 0.1);
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.045);
}

.participant-status {
  padding: 0.25rem 0.625rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
  line-height: 1.3;
  white-space: nowrap;
}

.status-registered {
  border-color: rgba(88, 211, 136, 0.22);
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.12);
}

.status-waiting {
  border-color: rgba(244, 208, 111, 0.22);
  color: #f4d06f;
  background: rgba(245, 158, 11, 0.1);
}

.status-attended {
  border-color: rgba(125, 211, 252, 0.22);
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.1);
}

.status-no-show,
.status-cancelled {
  border-color: rgba(255, 255, 255, 0.11);
  color: #b8b8bd;
  background: rgba(255, 255, 255, 0.055);
}

.participant-actions {
  display: flex;
  grid-column: 2 / -1;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4375rem;
  padding-top: 0.125rem;
}

.attendance-action,
.participant-reset-action,
.participant-cancel-action {
  min-height: 2.75rem !important;
  border-radius: 0.6875rem !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.attendance-action--present {
  border-color: rgba(88, 211, 136, 0.25) !important;
  color: #7ee5a6 !important;
}

.attendance-action--absent,
.participant-reset-action {
  border-color: rgba(255, 255, 255, 0.13) !important;
  color: #c7c7cb !important;
}

.participant-cancel-action {
  margin-left: auto;
  color: #ff9aa4 !important;
}

.participants-empty {
  display: flex;
  min-height: 14rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 0.9375rem;
  text-align: center;
}

.participants-empty > span {
  display: grid;
  width: 3.25rem;
  height: 3.25rem;
  place-items: center;
  border-radius: 0.9375rem;
  color: #8f9095;
  background: rgba(255, 255, 255, 0.055);
}

.participants-empty > span .v-icon {
  color: currentColor;
}

.participants-empty h3 {
  margin: 0.75rem 0 0;
  color: #d4d4d8;
  font-size: 1rem;
}

.participants-empty p {
  margin: 0.375rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.delete-dialog-card {
  padding: 1.25rem;
  border: 1px solid rgba(239, 83, 80, 0.24) !important;
  border-radius: 1.25rem !important;
  background: linear-gradient(145deg, rgba(239, 83, 80, 0.07), transparent 48%), #1a1c1b !important;
  text-align: center;
}

.delete-dialog-icon {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto;
  place-items: center;
  border-radius: 1rem;
  color: #ff9aa4;
  background: rgba(239, 83, 80, 0.13);
}

.delete-dialog-icon .v-icon {
  color: currentColor;
  font-size: 1.75rem;
}

.delete-dialog-copy > div {
  margin-top: 0.875rem;
  color: #ff9aa4;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.delete-dialog-copy h2 {
  margin: 0.25rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.3;
}

.delete-dialog-copy p {
  margin: 0.5rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.delete-dialog-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0 !important;
}

.confirm-delete-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  color: #ffffff !important;
  font-size: 0.75rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 46rem) {
  .manager-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .create-action {
    width: 100%;
  }

  .form-grid--two,
  .form-grid--time,
  .registration-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .grid-span-full {
    grid-column: auto;
  }

  .participants-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .registration-filter,
  .export-action {
    width: 100%;
  }

  .registration-filter {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .registration-filter ::v-deep .v-btn {
    width: 100%;
    min-width: 0 !important;
  }
}

@media (max-width: 37.4375rem) {
  .manager-toolbar {
    gap: 0.875rem;
    padding: 0.875rem;
    border-radius: 1rem;
  }

  .manager-heading {
    align-items: flex-start;
    gap: 0.75rem;
  }

  .manager-heading-icon {
    flex-basis: 2.75rem;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.8125rem;
  }

  .manager-heading h2 {
    font-size: 1.25rem;
  }

  .manager-heading p {
    font-size: 0.875rem;
  }

  .manager-filter {
    display: block;
    margin: 0.625rem 0 0.875rem;
    padding: 0.375rem;
    border-radius: 0.875rem;
  }

  .manager-filter > span {
    display: none;
  }

  .filter-toggle {
    display: grid !important;
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-toggle ::v-deep .v-btn {
    width: 100%;
    padding: 0 0.375rem !important;
    font-size: 0.75rem !important;
  }

  .filter-toggle ::v-deep .v-btn__content {
    min-width: 0;
    justify-content: center;
    letter-spacing: 0;
  }

  .filter-toggle ::v-deep .v-icon {
    margin-right: 0.25rem !important;
  }

  .session-list {
    margin: -0.25rem !important;
  }

  .session-column {
    padding: 0.25rem !important;
  }

  .tile-header {
    grid-template-columns: 3.5rem minmax(0, 1fr);
  }

  .phase-pill {
    grid-column: 2;
    justify-self: start;
  }

  .tile-facts {
    grid-template-columns: minmax(0, 1fr);
  }

  .empty-state {
    min-height: 13rem;
    padding: 1.25rem;
    border-radius: 0.9375rem;
  }

  .termin-dialog-card,
  .participants-dialog-card {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .termin-dialog-header,
  .participants-dialog-header {
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
    gap: 0.75rem;
    padding: 0.875rem;
  }

  .termin-dialog-icon,
  .participants-dialog-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.8125rem;
  }

  .termin-dialog-title h2 {
    font-size: 1.125rem;
  }

  .termin-dialog-title p {
    display: none;
  }

  .participants-dialog-title h2 {
    font-size: 1.125rem;
  }

  .participants-dialog-title p {
    display: none;
  }

  .termin-dialog-body {
    padding: 0.75rem !important;
  }

  .form-section {
    padding: 0.875rem;
    border-radius: 0.9375rem;
  }

  .form-section-heading {
    align-items: flex-start;
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }

  .form-section-heading > span {
    flex-basis: 2.25rem;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.6875rem;
  }

  .setting-control,
  .automation-heading {
    align-items: center;
  }

  .registration-option {
    padding: 0.75rem;
  }

  .registration-stats {
    gap: 0.375rem;
    padding: 0.625rem 0.75rem;
  }

  .registration-stat {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.25rem;
    text-align: center;
  }

  .registration-stat > span {
    width: 2.25rem;
    height: 2.25rem;
  }

  .registration-stat strong {
    font-size: 1.125rem;
  }

  .participants-dialog-body {
    padding: 0.75rem !important;
  }

  .registration-filter ::v-deep .v-btn {
    padding: 0 0.375rem !important;
  }

  .registration-filter ::v-deep .v-icon {
    margin-right: 0.25rem !important;
  }

  .participant-row {
    grid-template-columns: 2.75rem minmax(0, 1fr);
    gap: 0.625rem;
    padding: 0.75rem;
  }

  .participant-status {
    grid-column: 2;
    justify-self: start;
  }

  .participant-actions {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.375rem;
  }

  .participant-actions .v-btn {
    width: 100%;
    min-width: 0 !important;
    padding: 0 0.375rem !important;
  }

  .participant-cancel-action {
    margin-left: 0;
  }

  .delete-dialog-card {
    padding: 1rem;
    border-radius: 1.0625rem !important;
  }

  .termin-dialog-actions {
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 0.5rem;
    padding: 0.75rem !important;
  }

  .cancel-action,
  .save-action {
    width: 100%;
    min-width: 0 !important;
    padding: 0 0.5rem !important;
  }
}

@media (max-width: 20rem) {
  .filter-toggle ::v-deep .v-icon,
  .registration-filter ::v-deep .v-icon {
    display: none;
  }

  .termin-dialog-actions,
  .delete-dialog-actions {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
